import { useState, useRef, useCallback } from "react";
import {
  uploadImage,
  ALLOWED_IMAGE_TYPES,
  MAX_IMAGE_SIZE,
} from "../../api/upload.api";

interface UploadedImage {
  key: string;
  publicUrl: string;
  preview: string;
}

interface ImageUploadProps {
  folder: "posts" | "avatars" | "covers";
  maxFiles?: number;
  onUploadComplete: (images: UploadedImage[]) => void;
  /** Existing images to display (for edit mode) */
  existingImages?: UploadedImage[];
}

interface PendingFile {
  file: File;
  preview: string;
  progress: number;
  status: "pending" | "uploading" | "done" | "error";
  error?: string;
  result?: { key: string; publicUrl: string };
}

export function ImageUpload({
  folder,
  maxFiles = 10,
  onUploadComplete,
  existingImages = [],
}: ImageUploadProps) {
  const [files, setFiles] = useState<PendingFile[]>([]);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const totalImages = existingImages.length + files.length;

  function validateFile(file: File): string | null {
    if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
      return "Invalid file type. Use JPEG, PNG, WebP, or GIF.";
    }
    if (file.size > MAX_IMAGE_SIZE) {
      return "File too large. Maximum size is 10MB.";
    }
    return null;
  }

  const addFiles = useCallback(
    (newFiles: FileList | File[]) => {
      const fileArray = Array.from(newFiles);
      const remaining = maxFiles - totalImages;

      if (remaining <= 0) return;

      const toAdd = fileArray.slice(0, remaining).map((file) => {
        const error = validateFile(file);
        return {
          file,
          preview: URL.createObjectURL(file),
          progress: 0,
          status: error ? ("error" as const) : ("pending" as const),
          error: error ?? undefined,
        };
      });

      setFiles((prev) => [...prev, ...toAdd]);
    },
    [maxFiles, totalImages]
  );

  function removeFile(index: number) {
    setFiles((prev) => {
      const removed = prev[index];
      URL.revokeObjectURL(removed.preview);
      return prev.filter((_, i) => i !== index);
    });
  }

  function handleDrop(e: React.DragEvent) {
    e.preventDefault();
    setDragOver(false);
    if (e.dataTransfer.files.length > 0) {
      addFiles(e.dataTransfer.files);
    }
  }

  async function handleUpload() {
    const pendingFiles = files.filter((f) => f.status === "pending");
    if (pendingFiles.length === 0) return;

    setUploading(true);

    const updatedFiles = [...files];

    for (let i = 0; i < updatedFiles.length; i++) {
      const pf = updatedFiles[i];
      if (pf.status !== "pending") continue;

      updatedFiles[i] = { ...pf, status: "uploading", progress: 0 };
      setFiles([...updatedFiles]);

      try {
        const result = await uploadImage(folder, pf.file, (percent) => {
          updatedFiles[i] = { ...updatedFiles[i], progress: percent };
          setFiles([...updatedFiles]);
        });

        updatedFiles[i] = {
          ...updatedFiles[i],
          status: "done",
          progress: 100,
          result,
        };
        setFiles([...updatedFiles]);
      } catch {
        updatedFiles[i] = {
          ...updatedFiles[i],
          status: "error",
          error: "Upload failed",
        };
        setFiles([...updatedFiles]);
      }
    }

    setUploading(false);

    // Collect all successfully uploaded images
    const uploaded: UploadedImage[] = [
      ...existingImages,
      ...updatedFiles
        .filter((f) => f.status === "done" && f.result)
        .map((f) => ({
          key: f.result!.key,
          publicUrl: f.result!.publicUrl,
          preview: f.preview,
        })),
    ];

    onUploadComplete(uploaded);
  }

  const allDone = files.length > 0 && files.every((f) => f.status === "done" || f.status === "error");
  const hasPending = files.some((f) => f.status === "pending");

  return (
    <div className="space-y-3">
      {/* Drop zone */}
      {totalImages < maxFiles && (
        <div
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={handleDrop}
          onClick={() => inputRef.current?.click()}
          className={`cursor-pointer rounded-lg border-2 border-dashed p-6 text-center transition-colors ${
            dragOver
              ? "border-gray-900 bg-gray-50"
              : "border-gray-300 hover:border-gray-400"
          }`}
        >
          <p className="text-sm text-gray-600">
            {folder === "posts"
              ? "Drop images here or click to select"
              : "Drop an image here or click to select"}
          </p>
          <p className="mt-1 text-xs text-gray-400">
            JPEG, PNG, WebP, or GIF · Max 10MB
            {maxFiles > 1 && ` · Up to ${maxFiles} images`}
          </p>
          <input
            ref={inputRef}
            type="file"
            accept={ALLOWED_IMAGE_TYPES.join(",")}
            multiple={maxFiles > 1}
            onChange={(e) => {
              if (e.target.files) addFiles(e.target.files);
              e.target.value = "";
            }}
            className="hidden"
          />
        </div>
      )}

      {/* Previews */}
      {(existingImages.length > 0 || files.length > 0) && (
        <div className="grid grid-cols-3 gap-2 sm:grid-cols-4">
          {/* Existing images */}
          {existingImages.map((img, i) => (
            <div key={`existing-${i}`} className="relative aspect-square">
              <img
                src={img.publicUrl}
                alt=""
                className="h-full w-full rounded-lg object-cover"
              />
            </div>
          ))}

          {/* New files */}
          {files.map((pf, i) => (
            <div key={i} className="relative aspect-square">
              <img
                src={pf.preview}
                alt=""
                className={`h-full w-full rounded-lg object-cover ${
                  pf.status === "error" ? "opacity-50" : ""
                }`}
              />

              {/* Progress overlay */}
              {pf.status === "uploading" && (
                <div className="absolute inset-0 flex items-center justify-center rounded-lg bg-black/40">
                  <span className="text-sm font-medium text-white">
                    {pf.progress}%
                  </span>
                </div>
              )}

              {/* Done checkmark */}
              {pf.status === "done" && (
                <div className="absolute bottom-1 right-1 flex h-5 w-5 items-center justify-center rounded-full bg-green-500 text-xs text-white">
                  ✓
                </div>
              )}

              {/* Error indicator */}
              {pf.status === "error" && (
                <div className="absolute inset-x-0 bottom-0 rounded-b-lg bg-red-500/90 px-1 py-0.5 text-center text-xs text-white">
                  {pf.error}
                </div>
              )}

              {/* Remove button */}
              {(pf.status === "pending" || pf.status === "error") && (
                <button
                  onClick={(e) => { e.stopPropagation(); removeFile(i); }}
                  className="absolute top-1 right-1 flex h-5 w-5 items-center justify-center rounded-full bg-black/60 text-xs text-white hover:bg-black/80"
                >
                  ×
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Upload button */}
      {hasPending && (
        <button
          onClick={handleUpload}
          disabled={uploading}
          className="w-full rounded-lg bg-gray-900 px-4 py-2.5 text-sm font-medium text-white hover:bg-gray-800 disabled:opacity-50"
        >
          {uploading
            ? "Uploading..."
            : `Upload ${files.filter((f) => f.status === "pending").length} image${files.filter((f) => f.status === "pending").length > 1 ? "s" : ""}`}
        </button>
      )}

      {/* Status message */}
      {allDone && (
        <p className="text-center text-sm text-green-600">
          {files.filter((f) => f.status === "done").length} image{files.filter((f) => f.status === "done").length > 1 ? "s" : ""} uploaded
        </p>
      )}
    </div>
  );
}

export type { UploadedImage };
